import request from "./../utils/request";

// Request Deduplication Layer
const pendingRequests = new Map();
const requestCache = new Map();
const CACHE_TTL = 5000; // 5 saniye cache

function getRequestKey(url, params) {
    const sortedParams = params ? JSON.stringify(params, Object.keys(params).sort()) : '';
    return `${url}_${sortedParams}`;
}

function cachedRequest(requestFn, url, params, ttl = CACHE_TTL) {
    const key = getRequestKey(url, params);

    // Cache'de varsa hemen döndür
    const cached = requestCache.get(key);
    if (cached && Date.now() - cached.timestamp < ttl) {
        return Promise.resolve(cached.data);
    }

    // Aynı istek pending ise beklet
    if (pendingRequests.has(key)) {
        return pendingRequests.get(key);
    }

    // Yeni istek at
    const promise = requestFn()
        .then(response => {
            requestCache.set(key, {
                data: response,
                timestamp: Date.now()
            });
            pendingRequests.delete(key);
            return response;
        })
        .catch(error => {
            pendingRequests.delete(key);
            throw error;
        });

    pendingRequests.set(key, promise);
    return promise;
}

// Cache'i temizle
export function invalidateTaskCache() {
    requestCache.clear();
}

// Retry mekanizması - 5 kereye kadar dener
async function retryRequest(requestFn, maxRetries = 5) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const result = await requestFn();
            return result;
        } catch (error) {
            lastError = error;

            // AbortError veya CanceledError ise retry yapma
            if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                throw error;
            }

            // Son deneme ise hata fırlat
            if (attempt === maxRetries) {
                throw error;
            }

            // Bir sonraki deneme için devam et
        }
    }

    throw lastError;
}

// Abort controller for cancelled requests
let currentWorkflowAbortController = null;

export function getAllWorkFlows() {
    return request({
        url: "/tasks/workflows?version=v2",
        method: "get",
    });
}

export function getWorkflowPipelines(workflowId, pipelines, filters = {}) {
    const params = {};

    if (pipelines && pipelines.length > 0) {
        params.pipelines = pipelines;
    }

    if (filters.assignees && filters.assignees.length > 0) {
        params.assignees = filters.assignees;
    }
    if (filters.showOverdue !== undefined) {
        params.showOverdue = filters.showOverdue;
    }
    if (filters.search) {
        params.search = filters.search;
    }
    if (filters.priorities && filters.priorities.length > 0) {
        params.priorities = filters.priorities;
    }
    if (filters.owner && filters.owner.length > 0) {
        params.owner = filters.owner;
    }
    if (filters.followers && filters.followers.length > 0) {
        params.followers = filters.followers;
    }
    if (filters.dateRange) {
        params.dateRange = filters.dateRange;
    }
    if (filters.createdBy && filters.createdBy.length > 0) {
        params.createdBy = filters.createdBy;
    }
    if (filters.tags && filters.tags.length > 0) {
        params.tags = filters.tags;
    }
    if (filters.startDateRange) {
        params.startDateRange = filters.startDateRange;
    }
    if (filters.endDateRange) {
        params.endDateRange = filters.endDateRange;
    }
    if (filters.updateAtRange) {
        params.updateAtRange = filters.updateAtRange;
    }

    return request({
        url: `/tasks/workflows/${workflowId}/pipelines`,
        method: "get",
        params,
    });
}

export function getWorkflowTasksWithPipelines(workflowId, filters = {}) {
    const params = {};

    if (filters.page) params.page = filters.page;
    if (filters.perPage) params.perPage = filters.perPage;
    if (filters.pipelines && filters.pipelines.length > 0) {
        params.pipelines = filters.pipelines;
    }
    if (filters.assignees && filters.assignees.length > 0) {
        params.assignees = filters.assignees;
    }
    if (filters.showOverdue !== undefined) {
        params.showOverdue = filters.showOverdue;
    }
    if (filters.search) {
        params.search = filters.search;
    }
    if (filters.priorities && filters.priorities.length > 0) {
        params.priorities = filters.priorities;
    }
    if (filters.owner && filters.owner.length > 0) {
        params.owner = filters.owner;
    }
    if (filters.followers && filters.followers.length > 0) {
        params.followers = filters.followers;
    }
    if (filters.dateRange) {
        params.dateRange = filters.dateRange;
    }
    if (filters.createdBy && filters.createdBy.length > 0) {
        params.createdBy = filters.createdBy;
    }
    if (filters.tags && filters.tags.length > 0) {
        params.tags = filters.tags;
    }
    if (filters.startDateRange) {
        params.startDateRange = filters.startDateRange;
    }
    if (filters.endDateRange) {
        params.endDateRange = filters.endDateRange;
    }
    if (filters.updateAtRange) {
        params.updateAtRange = filters.updateAtRange;
    }

    // Önceki workflow isteğini iptal et
    if (currentWorkflowAbortController) {
        currentWorkflowAbortController.abort();
    }

    currentWorkflowAbortController = new AbortController();
    const signal = currentWorkflowAbortController.signal;

    // Retry mekanizması ile istek at (5 kereye kadar dener)
    return retryRequest(() =>
        cachedRequest(
            () => request({
                url: `/tasks/workflows/${workflowId}/full`,
                method: "get",
                params,
                signal
            }),
            `/tasks/workflows/${workflowId}/full`,
            params
        )
    ).finally(() => {
        if (currentWorkflowAbortController && currentWorkflowAbortController.signal === signal) {
            currentWorkflowAbortController = null;
        }
    });
}

export function getWorkflowTasks(workflowId, assignees, showOverdue, page = 1, perPage = 15, search, priorities, owner, followers, dateRange, createdBy, tags, startDateRange, endDateRange, updateAtRange, pipelines) {
    return request({
        url: `/tasks/${workflowId}`,
        method: "get",
        params: {
            assignees,
            page,
            perPage,
            showOverdue,
            search,
            priorities,
            pipelines,
            owner,
            followers,
            dateRange,
            createdBy,
            tags,
            startDateRange,
            endDateRange,
            updateAtRange
        },
    });
}

export function getPipelineTasks(workflowId, pipelineId, assignees, showOverdue, page = 1, perPage = 15, owner, followers, dateRange, createdBy, tags, startDateRange, endDateRange, updateAtRange, pipelines) {
    return request({
        url: `/tasks/${workflowId}/${pipelineId}`,
        method: "get",
        params: {
            assignees,
            page,
            perPage,
            showOverdue,
            pipelines,
            owner,
            followers,
            dateRange,
            createdBy,
            tags,
            startDateRange,
            endDateRange,
            updateAtRange
        },
    });
}

export function updateTaskPosition(taskId, payload) {
    return request({
        url: `/tasks/${taskId}/position`,
        method: "patch",
        data: payload,
    });
}

export function updatePipelinePosition(workflowId, pipelineId, position) {
    return request({
        url: `/tasks/${workflowId}/${pipelineId}/position`,
        method: "patch",
        data: { position },
    });
}

export function createTask(payload) {
    return request({
        url: `/tasks`,
        method: "post",
        data: payload,
    });
}

export function createSimpleTask(payload) {
    return request({
        url: `/tasks/simple`,
        method: "post",
        data: payload,
    });
}

export function deleteTask(taskId) {
    return request({
        url: `/tasks/${taskId}`,
        method: "delete",
    });
}

export function createWorkflow(payload) {
    return request({
        url: `/tasks/workflows`,
        method: "post",
        data: payload,
    });
}

export function updateWorkflow(id, payload) {
    return request({
        url: `/tasks/workflows/${id}`,
        method: "patch",
        data: payload,
    });
}

export function getTaskDetail(taskId) {
    return request({
        url: `/tasks/${taskId}/detail`,
        method: "get",
    });
}

export function updateTask(taskId, payload) {
    return request({
        url: `/tasks/${taskId}`,
        method: "patch",
        data: payload,
    });
}

export function attachmentsDelete(taskId, attachmentId) {
    return request({
        url: taskId ? `/tasks/${taskId}/attachments/${attachmentId}` : `/tasks/attachments/${attachmentId}`,
        method: "delete",
    });
}

export function createPipelineStage(workflowId, payload) {
    return request({
        url: `/tasks/workflows/${workflowId}/pipelines`,
        method: "post",
        data: payload,
    });
}

export function updatePipelineStage(pipelineId, payload) {
    return request({
        url: `/tasks/pipelines/${pipelineId}`,
        method: "patch",
        data: payload,
    });
}

export function getListTasks(assignees, page, perPage, visitorId, tableRecord, showOverdue, search, priorities, owner, followers, dateRange, createdBy, tags, startDateRange, endDateRange, updateAtRange, workflowId, pipelines) {
    return request({
        url: `/tasks/list`,
        method: "get",
        params: {
            assignees,
            page,
            perPage,
            visitorId,
            tableRecord,
            showOverdue,
            search,
            priorities,
            pipelines,
            owner,
            followers,
            dateRange,
            createdBy,
            tags,
            startDateRange,
            endDateRange,
            updateAtRange,
            workflowId
        },
    });
}

export function deletePipelineStage(pipelineId, workflowId, newTaskPositionId) {
    return request({
        url: `/tasks/pipelines/${pipelineId}`,
        method: "delete",
        data: { workflowId, newTaskPositionId },
    });
}

export function getWorkflowDetails(workflowId) {
    return request({
        url: `/tasks/workflows/${workflowId}`,
        method: "get",
    });
}

export function multipleTaskDelete(taskIds) {
    return request({
        url: `/tasks/multiple/delete`,
        method: "post",
        data: {
            taskIds,
        },
    });
}

export function multipleTaskMove(pipelineId, taskIds) {
    return request({
        url: `/tasks/multiple/change-pipeline`,
        method: "post",
        data: {
            pipelineId,
            taskIds,
        },
    });
}

export function multipleTaskDone(taskIds) {
    return request({
        url: `/tasks/multiple/done`,
        method: "post",
        data: {
            taskIds,
        },
    });
}

export function deleteWorkflow(workflowId) {
    return request({
        url: `/tasks/workflows/${workflowId}`,
        method: "delete",
    });
}

export function listDeletedWorkflows() {
    return request({
        url: `/tasks/workflows/list-deleted`,
        method: "get",
    }).then((response) => response.data);
}

export function restoreWorkflow(workflowId) {
    return request({
        url: `/tasks/workflows/${workflowId}/restore-deleted`,
        method: "put",
    }).then((response) => response.data);
}

export function permanentlyDeleteWorkflow(workflowId) {
    return request({
        url: `/tasks/workflows/${workflowId}/permanently-delete`,
        method: "delete",
    }).then((response) => response.data);
}

export function getTaskListByDate(userId, startDate, endDate) {
    return request({
        url: `/tasks/get-event-list-by-date`,
        method: "get",
        params: {
            userId,
            startDate,
            endDate,
        },
    });
}

export function doneTask(taskId, status, description) {
    return request({
        url: `/tasks/${taskId}/done`,
        method: "patch",
        data: {
            status,
            description,
        },
    });
}

export function removeFromDoneTasks(taskId) {
    return request({
        url: `/tasks/${taskId}/done`,
        method: "patch",
    });
}

export function getListDoneTask(assignees, page, perPage, visitorId, search, priorities, owner, followers, dateRange, createdBy, tags, startDateRange, endDateRange, updateAtRange, showOverdue, workflowId) {
    return request({
        url: `/tasks/done-tasks`,
        method: "get",
        params: {
            assignees,
            page,
            perPage,
            visitorId,
            search,
            priorities,
            owner,
            followers,
            dateRange,
            createdBy,
            tags,
            startDateRange,
            endDateRange,
            updateAtRange,
            showOverdue,
            workflowId
        },
    });
}

export function archiveTask(workflowId, taskId, isArchived) {
    return request({
        url: `/tasks/${taskId}/archive`,
        method: "patch",
        data: { workflowId, isArchived },
    });
}

export function getArchiveTask(workflowId, page, perPage, archivedDateRange) {
    return request({
        url: `/tasks/workflows/${workflowId}/archive-tasks`,
        method: "get",
        params: {
            page,
            perPage,
            archivedDateRange
        },
    });
}

export function getDeleteTask(workflowId, page = 1, perPage = 20, deletedDateRange) {
    return request({
        url: `/tasks/workflows/${workflowId}/delete-task`,
        method: "get",
        params: {
            page,
            perPage,
            deletedDateRange
        },
    });
}

export function getTaskActivitys(taskId, page, limit) {
    return request({
        url: `/tasks/get-task-activity/${taskId}`,
        method: "get",
        params: { page: page, limit: limit },
    });
}

export function getDoneTask(workflowId, page, perPage, doneDateRange) {
    return request({
        url: `/tasks/workflows/${workflowId}/done-tasks`,
        method: "get",
        params: {
            page,
            perPage,
            doneDateRange
        },
    });
}

export function getListArchiveTasks(workflowId, page, perPage, archivedDateRange) {
    return request({
        url: `/tasks/archive-tasks`,
        method: "get",
        params: {
            workflowId,
            page,
            perPage,
            archivedDateRange
        },
    });
}

export function changeWorkflow(taskId, workflowId) {
    return request({
        url: `/tasks/${taskId}/change-workflow`,
        method: "patch",
        data: { workflowId },
    });
}

export function updateWorkflowOrder(workflows) {
    return request({
        url: `/tasks/workflows/order`,
        method: "post",
        data: { workflows },
    });
}

export function addTaskPermission({ workflowId, permissionType, userId }) {
    return request({
        url: `/tasks/workflows/${workflowId}/permissions/${permissionType}`,
        method: "put",
        data: {
            userId,
        },
    }).then((res) => res.data);
}

export function removeTaskPermission({ workflowId, permissionType, userId }) {
    return request({
        url: `/tasks/workflows/${workflowId}/permissions/${permissionType}/${userId}`,
        method: "delete",
    }).then((res) => res.data);
}

export function getVisitorTasks(siteId, visitorId) {
    return request({
        url: `/tasks/visitor-tasks`,
        method: "get",
        params: {
            siteId,
            visitorId,
        },
    });
}

export function getPiplineName(workflowId) {
    return request({
        url: `/tasks/pipelines`,
        method: "get",
        params: workflowId ? { workflowId } : {},
    });
}

export function getTaskReports(filters = {}) {
    return request({
        url: "/tasks/reports",
        method: "get",
        params: {
            startDate: filters.startDate,
            endDate: filters.endDate,
            users: filters.users,
            workflowId: filters.workflowId,
        },
    });
}

export function getTaskReportsTimeline(userId, filters = {}) {
    return request({
        url: "/tasks/reports/timeline/" + userId,
        method: "get",
        params: {
            startDate: filters.startDate,
            endDate: filters.endDate,
            workflowId: filters.workflowId,
        },
    });
}
